import * as React from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { cn } from "../lib/cn.js";
import { Sparkline } from "./sparkline.js";

/**
 * StatCard — soft-tinted icon square + label + huge number + caption +
 * delta % + sparkline. Mirrors the stat tiles in the reference
 * screenshots (e.g. New Subscriptions / New Orders / Avg Order Revenue
 * tiles in Screenshot 003612.jpg).
 *
 * Uses oklch tints for the icon background so the four "tones" remain
 * legible across all 5 palettes without per-palette overrides.
 */

export type StatTone = "blue" | "green" | "violet" | "amber" | "rose" | "neutral";

const TONE_BG: Record<StatTone, string> = {
  blue: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  green: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  rose: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  neutral: "bg-muted text-muted-foreground",
};

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: string;
  value: React.ReactNode;
  caption?: string;
  icon?: React.ReactNode;
  tone?: StatTone;
  delta?: { value: string; trend: "up" | "down" | "flat" };
  trendData?: number[];
}

export function StatCard({
  label,
  value,
  caption,
  icon,
  tone = "neutral",
  delta,
  trendData,
  className,
  ...props
}: StatCardProps) {
  return (
    <article
      className={cn(
        "border-border bg-card group relative grid gap-3 rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md",
        className,
      )}
      {...props}
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon ? (
            <span
              aria-hidden="true"
              className={cn(
                "grid size-8 shrink-0 place-items-center rounded-md [&_svg]:size-4",
                TONE_BG[tone],
              )}
            >
              {icon}
            </span>
          ) : null}
          <p className="text-muted-foreground truncate text-sm font-medium">{label}</p>
        </div>
      </header>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-foreground text-3xl font-semibold tabular-nums leading-none tracking-tight">
            {value}
          </p>
          {caption ? <p className="text-muted-foreground mt-1.5 text-xs">{caption}</p> : null}
        </div>
        {trendData ? <Sparkline data={trendData} /> : null}
      </div>

      {delta ? (
        <footer className="flex items-center gap-1.5 text-xs font-medium">
          <span
            className={cn(
              "inline-flex items-center gap-0.5",
              delta.trend === "up" && "text-emerald-600 dark:text-emerald-400",
              delta.trend === "down" && "text-rose-600 dark:text-rose-400",
              delta.trend === "flat" && "text-muted-foreground",
            )}
          >
            {delta.trend === "up" ? (
              <ArrowUpRight className="size-3.5" aria-hidden="true" />
            ) : delta.trend === "down" ? (
              <ArrowDownRight className="size-3.5" aria-hidden="true" />
            ) : (
              <Minus className="size-3.5" aria-hidden="true" />
            )}
            {delta.value}
          </span>
        </footer>
      ) : null}
    </article>
  );
}
